import React, { useState, useMemo } from 'react';
import PreauditAnswerCard from './PreauditAnswerCard';

interface PreauditRightPanelProps {
  selectedItem: any;
  onNext: () => void;
  onPrev: () => void;
  onConfirm: (approvedAnswerId: string) => void;
  onRevoke: () => void;
  onManualSubmit: (text: string) => void;
}

const PreauditRightPanel: React.FC<PreauditRightPanelProps> = ({ 
  selectedItem, 
  onNext, 
  onPrev, 
  onConfirm,
  onRevoke,
  onManualSubmit
}) => {
  const [filterMode, setFilterMode] = useState<string>('all');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [selectedCandidateValue, setSelectedCandidateValue] = useState<string>('');
  const [approvedAnswerId, setApprovedAnswerId] = useState<string | null>(null);
  const [approvedAnswerValue, setApprovedAnswerValue] = useState<string>('');
  const [manualResponse, setManualResponse] = useState('');
  const [isManualAnswerApproved, setIsManualAnswerApproved] = useState(false);

  React.useEffect(() => {
    setIsManualAnswerApproved(false);
  }, [selectedItem?.id]);

  // Reset local state when a new item is selected
  React.useEffect(() => {
    setOpenDropdownId(null);
    setSelectedCandidateId(null);
    setSelectedCandidateValue('');
    
    const isApproved = selectedItem?.status?.toLowerCase() === 'approved';
    if (isApproved && selectedItem?.answers?.length > 0 && !isManualAnswerApproved) {
      setApprovedAnswerId(selectedItem.answers[0].id);
      setApprovedAnswerValue('Approved');
    } else {
      setApprovedAnswerId(null);
      setApprovedAnswerValue('');
    }
    
    setManualResponse('');
  }, [selectedItem?.id, selectedItem?.status, isManualAnswerApproved]);

  const answers = selectedItem?.answers || [];
  const isQuestionApproved = selectedItem?.status?.toLowerCase() === 'approved';

  const visibleAnswers = useMemo(() => {
    let result = [...answers];
    
    // If there's an approved answer, move it to the top
    if (approvedAnswerId) {
      const approvedIdx = result.findIndex(a => a.id === approvedAnswerId);
      if (approvedIdx > -1) {
        const [approvedAns] = result.splice(approvedIdx, 1);
        result.unshift(approvedAns);
      }
    }

    if (filterMode === 'best') return result.slice(0, 1);
    if (filterMode === 'top3') return result.slice(0, 3);
    return result;
  }, [answers, filterMode, approvedAnswerId]);

  const handleAnswerSelect = (ansId: string, value: string) => {
    if (approvedAnswerId) return; // Prevent change if already approved
    if (value === 'Choose appropriate') {
      if (selectedCandidateId === ansId) {
        setSelectedCandidateId(null);
        setSelectedCandidateValue('');
      }
    } else {
      setSelectedCandidateId(ansId);
      setSelectedCandidateValue(value);
    }
    setOpenDropdownId(null);
  };

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCandidateId(null);
    setSelectedCandidateValue('');
  };

  const handleConfirmClick = () => {
    if (selectedCandidateId) {
      setApprovedAnswerId(selectedCandidateId);
      setApprovedAnswerValue(selectedCandidateValue);
      setOpenDropdownId(null);
      // DO NOT clear selectedCandidateId here, so that revoking restores it.
      onConfirm(selectedCandidateId);
    }
  };

  const handleRevokeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setApprovedAnswerId(null);
    setApprovedAnswerValue('');
    // Notice we do NOT clear selectedCandidateId here.
    onRevoke();
  };

  const handleManualResponseSubmit = async () => {
    if (!manualResponse.trim()) return;
    await onManualSubmit(manualResponse);
    setIsManualAnswerApproved(true);
    setManualResponse('');
  };

  if (!selectedItem) {
    return (
      <div className="pa-right-panel" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#94a3b8' }}>Select a question to view details</p>
      </div>
    );
  }

  return (
    <div className="pa-right-panel">
      <div className="pa-right-panel-scrollable">
        <div className="pa-details-title-row">
          <h3 className="pa-details-title">{selectedItem.title}</h3>
        </div>

        <div className="pa-answer-header">
          <span className="pa-answer-count">{answers.length} Answers</span>
          <select 
            className="pa-answer-filter"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
          >
            <option value="all">All occurrences</option>
            <option value="top3">Top 3 answers</option>
            <option value="best">Best answer</option>
          </select>
        </div>

        <div className="pa-evidence-grid">
          {visibleAnswers.map((ans: any) => {
            const isApproved = ans.id === approvedAnswerId;
            const isSelectedCandidate = ans.id === selectedCandidateId;
            const displayValue = isApproved ? approvedAnswerValue : (isSelectedCandidate ? selectedCandidateValue : 'Choose appropriate');
            
            // Should this card show a trigger?
            // Yes, if it is the approved card.
            // If another card is approved, hide it.
            // If another card is selected, hide it.
            // If a dropdown is open on another card, hide it.
            let showTrigger = true;
            if (approvedAnswerId && !isApproved) showTrigger = false;
            if (selectedCandidateId && !isSelectedCandidate) showTrigger = false;

            return (
              <PreauditAnswerCard
                key={ans.id}
                answer={ans}
                isSelectedCandidate={isSelectedCandidate}
                isApproved={isApproved}
                selectedValue={displayValue}
                isOpen={openDropdownId === ans.id}
                showTrigger={showTrigger}
                onToggleOpen={(open) => setOpenDropdownId(open ? ans.id : null)}
                onSelect={(val) => handleAnswerSelect(ans.id, val)}
                onClearClick={handleClearClick}
                onRevokeClick={handleRevokeClick}
              />
            );
          })}
        </div>

        {!isQuestionApproved && !approvedAnswerId && !isManualAnswerApproved && (
          <div className="pa-manual-response">
            <div className="pa-manual-response-title">Manual response</div>
            <div className="pa-manual-input-wrapper">
              <input 
                type="text" 
                className="pa-manual-input"
                placeholder="Write manual response to add as an approved answer."
                value={manualResponse}
                onChange={(e) => setManualResponse(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualResponse.trim()) {
                    void handleManualResponseSubmit();
                  }
                }}
              />
              <button 
                className="pa-manual-submit"
                onClick={() => {
                  if (manualResponse.trim()) {
                    void handleManualResponseSubmit();
                  }
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pa-actions-footer">
        <div className="pa-footer-nav" onClick={onPrev}>Previous</div>
        <button 
          className="pa-btn-primary" 
          disabled={!selectedCandidateId || approvedAnswerId !== null}
          onClick={handleConfirmClick}
        >
          Confirm
        </button>
        <div className="pa-footer-nav" onClick={onNext}>Next</div>
      </div>
    </div>
  );
};

export default PreauditRightPanel;
